/**
 * Read-only change history panel for assumption config saves.
 * Opens from the provenance footer History control.
 * Admins may clear the operator-visible log (entries are archived server-side).
 */

import { useCallback, useEffect, useId, useState } from 'react';
import { History, X, ChevronDown, ChevronRight, Trash2 } from 'lucide-react';
import {
  clearConfigAssumptionsHistory,
  fetchConfigAssumptionsHistory,
} from '../../services/configAssumptionsHistoryClient';
import type {
  ConfigHistoryEntry,
  ConfigHistoryKind,
} from '../../types/configAssumptionsHistory';
import { isSessionAdmin } from '../../utils/pageAccess';
import { Button } from '@/components/ui/button';
import { formatConfigUpdatedBy, isConfigRestoredDefaultsActor } from './ConfigMetaBadge';

function formatWhen(iso: string): string {
  try {
    return new Date(iso).toLocaleString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  } catch {
    return iso;
  }
}

function formatHistoryValue(value: string | number | boolean | null): string {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'number') {
    return Number.isInteger(value)
      ? value.toLocaleString()
      : value.toLocaleString(undefined, { maximumFractionDigits: 4 });
  }
  return String(value);
}

export interface ConfigChangeHistoryButtonProps {
  kind: ConfigHistoryKind;
  className?: string;
}

/** Compact control for the provenance footer line. */
export function ConfigChangeHistoryButton({ kind, className = '' }: ConfigChangeHistoryButtonProps) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => setOpen(true)}
        className={`shrink-0 text-chart-10/90 hover:text-chart-10 ${className}`}
      >
        <History className="w-3.5 h-3.5" aria-hidden />
        History
      </Button>
      {open ? <ConfigChangeHistoryPanel kind={kind} onClose={() => setOpen(false)} /> : null}
    </>
  );
}

export interface ConfigChangeHistoryPanelProps {
  kind: ConfigHistoryKind;
  onClose: () => void;
}

export default function ConfigChangeHistoryPanel({ kind, onClose }: ConfigChangeHistoryPanelProps) {
  const titleId = useId();
  const isAdmin = isSessionAdmin();
  const [loading, setLoading] = useState(true);
  const [clearing, setClearing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [clearError, setClearError] = useState<string | null>(null);
  const [entries, setEntries] = useState<ConfigHistoryEntry[]>([]);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    setClearError(null);
    try {
      const doc = await fetchConfigAssumptionsHistory(kind);
      setEntries(Array.isArray(doc.entries) ? doc.entries : []);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setEntries([]);
    } finally {
      setLoading(false);
    }
  }, [kind]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const toggle = (key: string) => {
    setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  // A previous clear's marker row alone is nothing to clear.
  const clearableCount = entries.filter((entry) => !entry.archived).length;

  const clearHistory = async () => {
    if (!isAdmin || clearing || clearableCount === 0) return;
    const ok = window.confirm(
      'Clear this change history for operators?\n\n'
        + 'Entries are archived for support and replaced here by a single '
        + '"earlier changes archived" row.',
    );
    if (!ok) return;
    setClearing(true);
    setClearError(null);
    try {
      await clearConfigAssumptionsHistory(kind);
      setExpanded({});
      await load();
    } catch (err) {
      setClearError(err instanceof Error ? err.message : String(err));
    } finally {
      setClearing(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-dropdown flex justify-end bg-black/60 backdrop-blur-sm"
      onClick={onClose}
      role="presentation"
    >
      <aside
        className="w-full max-w-md h-full bg-popover border-l border-border shadow-xl flex flex-col"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-border bg-card/80">
          <div className="min-w-0">
            <h2 id={titleId} className="text-sm font-semibold text-foreground">
              Change history
            </h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              Who changed which values on each save
            </p>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="iconSm"
            onClick={onClose}
            className="hover:bg-secondary"
            aria-label="Close change history"
          >
            <X className="w-4 h-4" />
          </Button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-2">
          {loading ? (
            <p className="text-xs text-muted-foreground text-center py-8">Loading history…</p>
          ) : error ? (
            <div className="space-y-2 text-center py-6">
              <p className="text-xs text-destructive">{error}</p>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => void load()}
              >
                Retry
              </Button>
            </div>
          ) : entries.length === 0 ? (
            <p className="text-xs text-muted-foreground text-center py-8 leading-relaxed">
              No earlier saves recorded yet. History starts from the next save.
            </p>
          ) : (
            entries.map((entry) => {
              const key = `${entry.revision}-${entry.saved_at}`;
              const performer = entry.performed_by ? ` · by ${entry.performed_by}` : '';
              if (entry.archived) {
                return (
                  <div
                    key={key}
                    className="rounded-lg border border-dashed border-accent bg-card/30 px-3 py-2.5"
                  >
                    <span className="block text-xs text-foreground font-medium">
                      {formatWhen(entry.saved_at)}
                    </span>
                    <span className="block text-xs text-muted-foreground mt-0.5">
                      {entry.summary} — earlier history is held by support{performer}
                    </span>
                  </div>
                );
              }
              const isOpen = Boolean(expanded[key]);
              const who = formatConfigUpdatedBy(entry.saved_by);
              const byLine = isConfigRestoredDefaultsActor(entry.saved_by)
                ? `Restored built-in defaults${performer}`
                : who
                  ? `by ${who}`
                  : null;
              return (
                <div
                  key={key}
                  className="rounded-lg border border-border bg-card/50 overflow-hidden"
                >
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => toggle(key)}
                    className="w-full h-auto items-start justify-start px-3 py-2.5 text-left hover:bg-card"
                    aria-expanded={isOpen}
                  >
                    <span className="mt-0.5 text-muted-foreground shrink-0">
                      {isOpen ? (
                        <ChevronDown className="w-3.5 h-3.5" aria-hidden />
                      ) : (
                        <ChevronRight className="w-3.5 h-3.5" aria-hidden />
                      )}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-xs text-foreground font-medium">
                        {formatWhen(entry.saved_at)}
                      </span>
                      {byLine ? (
                        <span className="block text-xs text-chart-10/90 mt-0.5 truncate">
                          {byLine}
                        </span>
                      ) : null}
                      <span className="block text-xs text-muted-foreground mt-0.5">
                        {entry.summary}
                      </span>
                    </span>
                  </Button>
                  {isOpen ? (
                    <div className="border-t border-border/80 px-3 py-2 bg-background/40">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="text-muted-foreground">
                            <th className="text-left font-medium py-1 pr-2">Field</th>
                            <th className="text-right font-medium py-1 px-1">Before</th>
                            <th className="text-right font-medium py-1 pl-1">After</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-muted">
                          {entry.changes.map((change) => (
                            <tr key={change.path}>
                              <td className="py-1.5 pr-2 text-foreground align-top">
                                {change.label}
                              </td>
                              <td className="py-1.5 px-1 text-right text-muted-foreground tabular-nums align-top whitespace-nowrap">
                                {formatHistoryValue(change.before)}
                              </td>
                              <td className="py-1.5 pl-1 text-right text-foreground tabular-nums align-top whitespace-nowrap">
                                {formatHistoryValue(change.after)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : null}
                </div>
              );
            })
          )}
        </div>

        {isAdmin && !loading && !error && clearableCount > 0 ? (
          <div className="border-t border-border px-4 py-3 bg-card/60 space-y-2">
            <Button
              type="button"
              variant="destructive"
              size="sm"
              loading={clearing}
              onClick={() => void clearHistory()}
              className="bg-destructive/50 text-destructive border border-destructive/50 hover:bg-destructive/60"
              title="Admin only — archive and hide this history from operators"
            >
              <Trash2 className="w-3.5 h-3.5" aria-hidden />
              Clear History
            </Button>
            {clearError ? (
              <p className="text-xs text-destructive" role="alert">
                {clearError}
              </p>
            ) : (
              <p className="text-xs text-muted-foreground leading-relaxed">
                Admin only. Removes setup noise before operators start editing.
              </p>
            )}
          </div>
        ) : null}
      </aside>
    </div>
  );
}
